import React from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ArrowRight, CaretDown, Plus} from '../../icons';
import {KLAVIYO_CHANNEL, MERCADO_ADS_CHANNEL, SHOPIFY_CHANNEL} from '../../channels/channels';
import {AnalyticsComponent} from '../analytics/AnalyticsComponent';
import {AccessRow} from './AccessRow';
import {StoreRow} from './StoreRow';
import {PlatformLogo} from './PlatformLogo';
import {accountsPathFor, isStoreChannel} from './platformsModel';
import {platformName, platformSummary, seeAccountsText, statusText} from './platformsText';
import {useShopifyInstallByLink} from '../../../context/UserModulesContext';

const NS = 'agency_v2.platforms';

const CHIP_BY_STATUS = {
    connected: '-ok',
    attention: '-err',
    expiring: '-warn',
    store_no_access: '-err',
    store_unassigned: '-warn',
};

/**
 * Una plataforma conectada, plegable: el encabezado dice cuántos accesos y cuentas tiene y en qué
 * estado está; desplegada, cada acceso (o cada tienda) en su fila, y al pie «Ver las N cuentas» y
 * «Agregar».
 *
 * GA4 por scripts (la integración vieja por planilla) no tiene accesos: desplegada monta el
 * `AnalyticsComponent` de siempre (contrato §2).
 */
export function PlatformRow({platform, open, onToggle, actions, expandCredentialId = null}) {
    const {t} = useTranslation();
    const {channel} = platform;
    const bodyId = `apl-platform-${channel}`;
    const status = statusText(t, platform);
    const seeText = seeAccountsText(t, platform);
    const isStore = isStoreChannel(channel);
    // Klaviyo se conecta con una API key: es un acceso, no una tienda, aunque viaje como tienda.
    const addLabel = isStore && channel !== KLAVIYO_CHANNEL ? t(`${NS}.add_store`) : t(`${NS}.add_access`);
    // Shopify por link de instalación (tanda 23/10): sin «Mandarle el link al cliente» y con el hint que nombra
    // su reemplazo («Copiar link para tu cliente», en el aviso de los pedidos).
    const {enabled: shopifyByLink} = useShopifyInstallByLink();

    return (
        <div className={`apl-platform${open ? ' -open' : ''}`} data-testid={`apl-platform-${channel}`}>
            <button type="button" className="apl-platform__head" aria-expanded={open} aria-controls={bodyId}
                    onClick={onToggle}>
                <PlatformLogo channel={channel}/>
                <span className="apl-platform__name">{platformName(channel)}</span>
                <span className="apl-platform__summary">{platformSummary(t, platform)}</span>
                {status && <span className={`apl-chip ${CHIP_BY_STATUS[platform.status.kind]}`}>{status}</span>}
                <CaretDown className="apl-platform__caret" size={16} aria-hidden="true"/>
            </button>

            {open && (
                <div className="apl-platform__body" id={bodyId}>
                    {platform.legacyGa4 ? (
                        <div className="apl-legacy">
                            <AnalyticsComponent/>
                        </div>
                    ) : (
                        <>
                            {platform.credentials.map((credential) => (
                                <AccessRow key={credential.id} channel={channel} credential={credential}
                                           initiallyExpanded={credential.id === expandCredentialId}
                                           onRename={(label) => actions.renameCredential(channel, credential.id, label)}
                                           onReconnect={() => actions.reconnect(channel, credential)}
                                           onRemove={() => actions.confirmRemoveCredential(channel, credential)}
                                           onSendLink={actions.sendMercadoAdsLink}/>
                            ))}
                            {platform.stores.map((store) => (
                                <StoreRow key={store.ad_account_id} store={store}
                                          onConnectClient={() => actions.connectStoreToClient(store)}
                                          onReauthorize={() => actions.reauthorizeStore(store)}
                                          onRemove={() => actions.confirmRemoveStore(store)}/>
                            ))}
                            {/* Reemplaza los dos tooltips del clásico que decían cuál de los dos botones
                                usar: «Mandarle el link» y «Agregar tienda» quedan juntos y sin esto no
                                se sabe cuál elegir (regla de fede: lo que sale lleva su reemplazo). */}
                            {channel === SHOPIFY_CHANNEL && (
                                <p className="apl-hint" data-testid="apl-shopify-hint">
                                    {t(shopifyByLink ? 'shopify_link.platform_hint' : `${NS}.shopify_hint`)}
                                </p>
                            )}
                            <div className="apl-platform__foot">
                                {seeText && (
                                    <Link className="apl-link" to={accountsPathFor(channel)}>
                                        {seeText}
                                        <ArrowRight size={14} aria-hidden="true"/>
                                    </Link>
                                )}
                                <span className="apl-spacer"/>
                                {channel === SHOPIFY_CHANNEL && !shopifyByLink && (
                                    <button type="button" className="apl-btn -sm" onClick={actions.sendShopifyLink}>
                                        {t(`${NS}.send_link_client`)}
                                    </button>
                                )}
                                {/* Casi siempre autoriza el vendedor (sólo la cuenta principal puede): el link
                                    queda a mano también con la plataforma ya conectada. */}
                                {channel === MERCADO_ADS_CHANNEL && actions.sendMercadoAdsLink && (
                                    <button type="button" className="apl-btn -sm" data-testid="apl-mercado-ads-send-link"
                                            onClick={() => actions.sendMercadoAdsLink(null)}>
                                        {t('mercado_ads_link.choose.send_link')}
                                    </button>
                                )}
                                <button type="button" className="apl-btn -sm" onClick={() => actions.add(channel)}>
                                    <Plus size={15} aria-hidden="true"/>
                                    {addLabel}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default PlatformRow;
